import { Box, Group, Paper, Stack, Text, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import { forwardRef, ReactNode, useMemo } from 'react';
import { usePreferencesStore } from '../../../stores';
import { getContrastingTextColor } from '../../../utils/color';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type StatVariant = 'default' | 'gradient' | 'outlined' | 'minimal';

export interface StatProps {
  label: string;
  value: string | number | Date;
  description?: string;
  icon?: ReactNode;
  trend?: number;
  trendLabel?: string;
  variant?: StatVariant;
  gradientVariant?: GradientVariant;
  autoFormat?: boolean;
}

const Stat = forwardRef<HTMLDivElement, StatProps>(
  (
    {
      label,
      value,
      description,
      icon,
      trend,
      trendLabel,
      variant = 'default',
      gradientVariant = 'primary',
      autoFormat = true,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const gradient = theme.other.gradients[gradientVariant];
    const locale = usePreferencesStore((state) => state.locale);
    const timezone = usePreferencesStore((state) => state.timezone);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const TrendIcon = trend && trend > 0 ? IconArrowUpRight : trend && trend < 0 ? IconArrowDownRight : IconMinus;
    const trendColor = trend && trend > 0 ? 'teal' : trend && trend < 0 ? 'red' : 'gray';

    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const containerStyles: Record<StatVariant, React.CSSProperties> = {
      default: {
        backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
        border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
      },
      gradient: {
        backgroundImage: gradientBg,
        color: autoTextColor,
      },
      outlined: {
        backgroundColor: 'transparent',
        border: `2px solid ${gradient.from}`,
      },
      minimal: {
        backgroundColor: 'transparent',
        padding: 0,
      },
    };

    const formattedValue: string | number = useMemo((): string | number => {
      if (!autoFormat) return typeof value === 'object' ? String(value) : value;
      if (typeof value === 'number') {
        try {
          return new Intl.NumberFormat(locale || 'en-US').format(value);
        } catch {
          return value;
        }
      }
      if (value instanceof Date || (typeof value === 'string' && !Number.isNaN(Date.parse?.(value)))) {
        try {
          return new Intl.DateTimeFormat(locale || 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: timezone,
          }).format(new Date(value as any));
        } catch {
          return String(value);
        }
      }
      return typeof value === 'object' ? String(value) : value;
    }, [autoFormat, value, locale, timezone]);

    return (
      <Paper
        ref={ref}
        p={variant === 'minimal' ? 0 : 'md'}
        radius="md"
        style={{ ...containerStyles[variant], ...accessibilityStyles }}
      >
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <Text
              size="xs"
              tt="uppercase"
              fw={700}
              c={variant === 'gradient' ? autoTextColor : 'dimmed'}
              opacity={variant === 'gradient' ? 0.8 : 1}
            >
              {label}
            </Text>
            {icon && (
              <Box
                style={{
                  opacity: variant === 'gradient' ? 0.8 : 0.6,
                }}
              >
                {icon}
              </Box>
            )}
          </Group>

          <Text
            size="xl"
            fw={700}
            style={
              variant === 'outlined'
                ? {
                    backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }
                : undefined
            }
          >
            {formattedValue}
          </Text>

          {(trend !== undefined || description) && (
            <Group gap="xs">
              {trend !== undefined && (
                <Group gap={4}>
                  <TrendIcon
                    size={16}
                    color={variant === 'gradient' ? autoTextColor : theme.colors[trendColor][5]}
                    style={{ opacity: variant === 'gradient' ? 0.9 : 1 }}
                  />
                  <Text
                    size="sm"
                    fw={500}
                    c={variant === 'gradient' ? autoTextColor : trendColor}
                    opacity={variant === 'gradient' ? 0.9 : 1}
                  >
                    {Math.abs(trend)}%
                  </Text>
                </Group>
              )}
              {(trendLabel || description) && (
                <Text
                  size="xs"
                  c={variant === 'gradient' ? autoTextColor : 'dimmed'}
                  opacity={variant === 'gradient' ? 0.7 : 1}
                >
                  {trendLabel || description}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      </Paper>
    );
  }
);

Stat.displayName = 'Stat';

export default Stat;
